/**
 * quoteItems — a linha de `quote_items` gravada a partir de um item do
 * formulário, num lugar só.
 *
 * POR QUE ISTO EXISTE: o mesmo objeto era montado à mão em SEIS lugares
 * (NewQuotePage salva/atualiza/salva-como-novo, QuoteDetailPage
 * atualiza/offline/re-insere) — todos com a mesma conta de desconto e a mesma
 * LISTA EXPLÍCITA de colunas. Lista explícita é exatamente a armadilha #33 do
 * CLAUDE.md: coluna nova aparece na migration e na UI, o save "dá certo" (toast
 * de sucesso, sem erro de RLS) e o valor **nunca chega ao banco**. Foi o que
 * aconteceu com `companies.signature_url`, e o `unit_cost` teria seis chances de
 * repetir o mesmo bug.
 *
 * Aqui não há I/O: é função pura, testável, e é o que os testes de guarda usam.
 */
import { productUnitCost } from '@/lib/cost';
import { quoteItemLineTotal } from '@/lib/quoteTotals';
import type { ProductStockDeductionMode } from '@/lib/stockSettings';

/**
 * O mínimo que estas funções precisam de um item do formulário de orçamento
 * (`QuoteItem` de `SortableQuoteItemCard`) — declarado estruturalmente para
 * também aceitar as linhas soltas (`any`) que vêm do cache offline.
 */
export interface QuoteItemFormLike {
  name: string;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount?: number | null;
  discount_type?: 'value' | 'percent' | string | null;
  itemType?: 'service' | 'product' | string | null;
  item_type?: 'service' | 'product' | string | null;
  unit?: string | null;
  image_url?: string | null;
  /**
   * Fotos 2 e 3 do item (as outras duas do cartão de até 3). Chegaram ao
   * formulário e ao card antes de existir coluna no banco: o produto subia com
   * as 3 URLs, mas `quote_items` só tinha `image_url` — a 2ª e a 3ª eram
   * jogadas fora em silêncio, sem erro nem toast (#215/#216).
   */
  image_url_2?: string | null;
  image_url_3?: string | null;
  product_id?: string | null;
  barcode?: string | null;
  /** Snapshot do custo — ver {@link quoteItemUnitCost}. */
  unit_cost?: number | string | null;
  /**
   * Vínculo com a DESPESA COBRÁVEL que materializou esta linha
   * (`finance_entries.id`). Precisa atravessar o builder porque o save do
   * orçamento APAGA e REINSERE todos os itens: sem carregar a coluna, o
   * primeiro save depois de lançar a despesa quebraria o vínculo e a linha
   * viraria um item avulso que ninguém consegue mais editar nem remover pelo
   * financeiro — sem erro nenhum. Ver `src/lib/billableExpense.ts`.
   */
  finance_entry_id?: string | null;
  /**
   * ─── OS CAMPOS DE CADASTRO ────────────────────────────────────────────────
   *
   * Nenhum destes existe como coluna em `quote_items`: eles descrevem o PRODUTO,
   * não a linha do documento. Vivem no item do formulário porque o card de item
   * espelha o formulário do catálogo, e é deles que sai o produto criado pelo
   * "salvar este item no meu catálogo". Ao reabrir um orçamento salvo eles voltam
   * do próprio catálogo (a hidratação por `product_id` do `QuoteDetailPage`).
   */
  category?: string | null;
  internal_code?: string | null;
  track_stock?: boolean | null;
  min_stock?: number | null;
  stock_deduction_mode?: ProductStockDeductionMode;
  lc116_item?: string | null;
  iss_aliquota?: number | null;
  iss_retido?: boolean;
  nbs?: string | null;
  cclasstrib?: string | null;
}

/** A linha de `quote_items` sem o `quote_id` (que o chamador conhece). */
export interface QuoteItemRow {
  name: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  unit_cost: number | null;
  discount: number;
  discount_type: 'value' | 'percent';
  item_type: 'service' | 'product';
  unit: string | null;
  image_url: string | null;
  image_url_2: string | null;
  image_url_3: string | null;
  total: number;
  position: number;
  product_id: string | null;
  barcode: string | null;
  finance_entry_id: string | null;
}

/**
 * Custo unitário que a linha grava — o SNAPSHOT que o item já carrega, nunca uma
 * leitura nova do produto.
 *
 * É a regra central da feature: o custo do produto muda com o tempo, então o
 * documento tem que guardar o custo do MOMENTO da venda. Se este valor fosse
 * recalculado a partir de `products.cost` na hora de salvar, editar um orçamento
 * antigo reescreveria o custo histórico dele com o de hoje — e todo relatório de
 * margem/CMV passado mudaria sozinho, sem erro nenhum na tela.
 *
 * O snapshot entra no item **quando ele é adicionado** ao documento (catálogo,
 * scanner, PDV) via {@link productUnitCost}, e sobrevive ao ciclo
 * carregar → editar → salvar porque os caminhos de leitura também o trazem.
 */
export function quoteItemUnitCost(item: Pick<QuoteItemFormLike, 'unit_cost'> | null | undefined): number | null {
  // Reaproveita a normalização de custo: `''`/`null`/negativo → null, 0 preservado.
  return productUnitCost({ cost: item?.unit_cost ?? null });
}

/**
 * Monta a linha de `quote_items` (sem `quote_id`) a partir de um item do
 * formulário. `productId` sobrescreve o `product_id` do item — usado quando o
 * save acabou de criar o produto no catálogo e só então conhece o id real.
 */
export function buildQuoteItemRow(
  item: QuoteItemFormLike,
  position: number,
  opts?: { productId?: string | null },
): QuoteItemRow {
  return {
    name: item.name,
    description: item.description || null,
    quantity: item.quantity,
    unit_price: item.unit_price,
    unit_cost: quoteItemUnitCost(item),
    discount: Number(item.discount) || 0,
    discount_type: item.discount_type === 'percent' ? 'percent' : 'value',
    item_type: (item.itemType || item.item_type) === 'product' ? 'product' : 'service',
    unit: item.unit || null,
    image_url: item.image_url || null,
    image_url_2: item.image_url_2 || null,
    image_url_3: item.image_url_3 || null,
    total: quoteItemLineTotal(item),
    position,
    product_id: opts?.productId || item.product_id || null,
    barcode: item.barcode || null,
    // Sobrevive ao ciclo apaga-e-reinsere do save; ver a nota em QuoteItemFormLike.
    finance_entry_id: quoteItemFinanceEntryId(item),
  };
}

/**
 * Vínculo com a despesa cobrável, normalizado.
 *
 * Um id temporário da fila offline (`offline_…`) NUNCA pode chegar ao Postgres
 * (erro `invalid input syntax for type uuid`), e vincular despesa cobrável é
 * online-only de propósito — a fila mapeia 1 operação → 1 uuid e não teria como
 * resolver o lançamento de uma linha criada offline (mesma razão de #30c/#65).
 * Então um id temporário degrada para "linha comum", não para um insert quebrado.
 */
export function quoteItemFinanceEntryId(
  item: Pick<QuoteItemFormLike, 'finance_entry_id'> | null | undefined,
): string | null {
  const raw = item?.finance_entry_id;
  if (!raw) return null;
  const id = String(raw);
  return id.startsWith('offline_') ? null : id;
}

/**
 * ─── "SALVAR ESTE ITEM NO MEU CATÁLOGO": AS COLUNAS DE CADASTRO ─────────────
 *
 * POR QUE ISTO EXISTE: o produto criado pelo checkbox era montado por LISTA
 * EXPLÍCITA de colunas em CINCO lugares (NewQuotePage salvar + salvar-como-novo
 * + cache, QuoteDetailPage online + offline + cache, `persistQuoteOffline`), e
 * as cinco listas eram DIFERENTES. Medido: o caminho offline e o
 * "salvar como novo" não levavam categoria, código interno, estoque nem
 * classificação fiscal — o usuário preenchia tudo no card, o save dizia
 * "sucesso", e o produto nascia com metade dos campos em branco. É a armadilha
 * #33/#62 na sua forma mais cara, porque aqui o dado perdido é o que o usuário
 * acabou de digitar.
 *
 * `price` e `cost` continuam ESCRITOS À MÃO em cada payload de propósito: o
 * guard de varredura (`saveToCatalogCost.test.ts`) ancora justamente em
 * `price: item.unit_price` e cobra `cost:` dentro do literal, e mover as duas
 * para cá desligaria a proteção contra o modo de falha que ela existe para pegar
 * (#75 — guard vence DRY quando os dois brigam).
 */
export interface CatalogProductColumns {
  description: string | null;
  type: 'service' | 'product';
  unit: string | null;
  category: string | null;
  internal_code: string | null;
  barcode: string | null;
  track_stock: boolean;
  min_stock: number | null;
  stock_deduction_mode: ProductStockDeductionMode;
  lc116_item: string | null;
  iss_aliquota: number | null;
  /** ISS retido na fonte. Vira `tpRetISSQN=2` no DPS da NFS-e. */
  iss_retido: boolean;
  nbs: string | null;
  cclasstrib: string | null;
}

/** Tipo do produto criado a partir do item — serviço é o padrão do app. */
export function quoteItemProductType(
  item: Pick<QuoteItemFormLike, 'itemType' | 'item_type'> | null | undefined,
): 'service' | 'product' {
  return (item?.itemType || item?.item_type) === 'product' ? 'product' : 'service';
}

/**
 * Número opcional vindo do formulário. `0` é PRESERVADO — um estoque mínimo de
 * zero e uma alíquota de zero (isento) são valores informados, e o `|| null` que
 * os payloads usavam os transformava em "não informado" (a família do #61).
 */
function optionalNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function optionalText(raw: unknown): string | null {
  if (typeof raw !== 'string') return raw == null ? null : String(raw) || null;
  return raw.trim() || null;
}

/**
 * As colunas de cadastro do produto criado a partir de um item do orçamento.
 *
 * A normalização por TIPO é a mesma do formulário do catálogo, e é regra de
 * negócio, não arrumação: serviço não tem estoque nem código de barras, produto
 * não tem classificação de LC 116. Gravar o campo do outro tipo criaria um
 * produto que o formulário do catálogo não conseguiria reproduzir.
 *
 * `hasVariants`: o PAI de um grupo de variações não é vendável nem estocável
 * (#30) — quem carrega saldo, código de barras e código interno é a filha.
 */
export function catalogProductColumnsFromQuoteItem(
  item: QuoteItemFormLike,
  opts?: { hasVariants?: boolean },
): CatalogProductColumns {
  const type = quoteItemProductType(item);
  const isService = type === 'service';
  const hasVariants = opts?.hasVariants === true;
  const tracked = !isService && !hasVariants && item.track_stock === true;

  return {
    description: optionalText(item.description),
    type,
    unit: item.unit || null,
    category: optionalText(item.category),
    internal_code: hasVariants ? null : optionalText(item.internal_code),
    barcode: isService || hasVariants ? null : optionalText(item.barcode),
    track_stock: tracked,
    min_stock: tracked ? optionalNumber(item.min_stock) : null,
    stock_deduction_mode: isService ? null : normalizeStockDeductionMode(item.stock_deduction_mode),
    lc116_item: isService ? optionalText(item.lc116_item) : null,
    iss_aliquota: isService ? optionalNumber(item.iss_aliquota) : null,
    // Booleano com NOT NULL no banco: produto nunca e `null` aqui, e `false`
    // reproduz o comportamento de antes da coluna existir.
    iss_retido: isService ? item.iss_retido === true : false,
    nbs: isService ? optionalText(item.nbs) : null,
    cclasstrib: isService ? optionalText(item.cclasstrib) : null,
  };
}

/** Vocabulário fechado; qualquer outra coisa é "herdar da empresa" (`null`). */
export function normalizeStockDeductionMode(raw: unknown): ProductStockDeductionMode {
  return raw === 'off' || raw === 'ask' || raw === 'auto' ? raw : null;
}

/** A mesma linha, já endereçada a um orçamento — o que vai para o `.insert()`. */
export function buildQuoteItemInsert(
  item: QuoteItemFormLike,
  position: number,
  quoteId: string,
  opts?: { productId?: string | null },
): QuoteItemRow & { quote_id: string } {
  return { quote_id: quoteId, ...buildQuoteItemRow(item, position, opts) };
}
